/**
 * P2.2 — meter detail. One page per meter carrying >=10 hymns (~20 routes).
 *
 * This is the substitution tool, and the reason the meter axis exists at all: a text
 * in Common Metre can be sung to any CM tune, so "what else fits this text" is the
 * question a musician actually arrives with. LM alone covers 125 hymns.
 */
import { useParams, Link } from 'react-router-dom'

import { HYMNAL_1982, index } from '@/data/hymnal'
import { Crumb, HymnList, SectionTitle } from '@/components/HymnList'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'

export default function MeterPage() {
  const { slug } = useParams<{ slug: string }>()
  const meter = slug ? index.meterBySlug.get(slug) : undefined

  if (!meter) {
    return (
      <NotFound
        title="No such meter page"
        detail="Only meters used by ten or more hymns have a page of their own."
        backTo={`/hymnal/${HYMNAL_1982.id}`}
        backLabel="Browse The Hymnal 1982"
      />
    )
  }

  // Distinct tunes in this meter — the interchangeable set.
  const tunes = [
    ...new Map(
      meter.hymns
        .map((ref) => index.hymnBySlug.get(ref.slug))
        .filter((h): h is NonNullable<typeof h> => Boolean(h?.tuneSlug))
        .map((h) => [h.tuneSlug!, { slug: h.tuneSlug!, name: h.tuneName! }])
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name))

  const paged = tunes.filter((t) => index.tuneBySlug.has(t.slug))

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title={`${meter.meter} — metre`}
        description={`Hymns and tunes in ${meter.meter} from The Hymnal 1982. Any text in this metre can be sung to any tune in it.`}
        path={`/hymnal/${HYMNAL_1982.id}/meter/${meter.slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${meter.meter} — hymns and tunes in this metre`,
          isPartOf: { '@type': 'WebSite', name: HYMNAL_1982.label },
          // A metre is not a MusicComposition — it is the index over the ones that
          // share it, which is what makes CollectionPage the honest type here.
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: meter.hymns.length,
            itemListElement: meter.hymns.slice(0, 25).map((h, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: h.title,
              url: `https://fuguelab.com/hymnal/${HYMNAL_1982.id}/hymn/${h.slug}`,
            })),
          },
        }}
      />
      <Crumb label={meter.meter} />

      <header className="mb-8">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Metre</p>
        <h1 className="text-3xl sm:text-4xl font-display text-foreground mb-3">{meter.meter}</h1>
        <p className="text-muted-foreground max-w-2xl">
          {meter.hymns.length} hymns and {tunes.length} tunes share this metre. A text in {meter.meter} can be sung to
          any of them — which is what makes a substitution possible when a tune is unfamiliar to the congregation.
        </p>
      </header>

      {paged.length > 0 && (
        <section className="mb-8">
          <SectionTitle>Tunes in this metre</SectionTitle>
          <ul className="flex flex-wrap gap-2">
            {paged.map((t) => (
              <li key={t.slug}>
                <Link
                  to={`/hymnal/${HYMNAL_1982.id}/tune/${t.slug}`}
                  className="inline-block rounded border border-border px-2.5 py-1 text-xs tracking-wide text-muted-foreground hover:border-border-strong hover:text-foreground"
                >
                  {t.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <SectionTitle>Hymns in this metre</SectionTitle>
        <HymnList hymns={meter.hymns} />
      </section>
    </article>
  )
}
